// src/components/Dashboard/RoleDashboard/RoleExtras/RoleChats.jsx

import React, { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../../../../config";
import { apiFetch } from "../../../../apiFetch";
import { useLocation, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import "./RoleChats.css";
import { resolveId } from "../roleApi";

const VALID_ROLES = ["agent", "scout", "club"];

function normalizeRole(r) {
    const role = String(r || "").toLowerCase();
    return VALID_ROLES.includes(role) ? role : "agent";
}

const RoleChats = ({
    role: roleProp,
    userId,
    id,
    agentId,
    scoutId,
    clubId,
    openConversationId = null,
    selectedPlayerId = null,
}) => {
    
    const location = useLocation();
    const navigate = useNavigate();


    /* -------------------- Role + Actor Resolution -------------------- */

    const role = normalizeRole(
        roleProp ||
        (typeof window !== "undefined" &&
            (localStorage.getItem("role") || localStorage.getItem("userRole"))) ||
        "agent"
    );

    const actorId = resolveId({
        role,
        id,
        userId,
        agentId,
        scoutId,
        clubId,
    });

    const parsedActorId = parseInt(actorId, 10) || 0;

    /* -------------------- State -------------------- */

    const socket = useRef(null);
    const typingTimeout = useRef(null);
    const lastTypedRef = useRef(0);
    const prevConvIdRef = useRef(null);
    const messagesEndRef = useRef(null);
    const activeConvIdRef = useRef(null);
    const lastMarkReadRef = useRef(null);
    const startGuardRef = useRef(null);



    const [isTyping, setIsTyping] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [partnerStatus, setPartnerStatus] = useState(null);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [underageGate, setUnderageGate] = useState({
        loading: false,
        approved: true,   // default allow unless we know we must block
        status: null,
    });

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth <= 768 : false
    );

    const [mobileListMode, setMobileListMode] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    /* -------------------- Helpers -------------------- */
    const sortConvs = (list) =>
        [...list].sort((a, b) => {
            const at = a.lastMessageTime
                ? new Date(a.lastMessageTime).getTime()
                : new Date(a.created_at).getTime();
            const bt = b.lastMessageTime
                ? new Date(b.lastMessageTime).getTime()
                : new Date(b.created_at).getTime();
            return bt - at;
        });

    const resolvedOpenConversationId =
        openConversationId ?? location.state?.openConversationId ?? null;

    const resolvedSelectedPlayerId =
        selectedPlayerId ?? location.state?.selectedPlayerId ?? null;



    const getPartnerSlug = (conv) => {
        if (!conv) return null;
        return conv.partnerProfileSlug || conv.partnerSlug || null;
    };


    const navigateToPlayerProfile = (conv = activeConversation) => {
        const slug = getPartnerSlug(conv);
        if (!slug) return;

        const r = (role || "agent").toLowerCase();

        navigate(`/${r}-dashboard/player-profile/${slug}`, {
            state: {
                viewerRole: r,
                viewerId: parsedActorId,
                from: "chat",
            },
        });
    };


    const actorHasSentFirstMessage = React.useMemo(
        () =>
            messages.some(
                (msg) => parseInt(msg.sender_id, 10) === parsedActorId
            ),
        [messages, parsedActorId]
    );


    const isPendingAndInitiatedByActor = React.useMemo(
        () =>
            activeConversation?.status === "pending" &&
            activeConversation?.initiated_by === parsedActorId &&
            actorHasSentFirstMessage,
        [activeConversation, parsedActorId, actorHasSentFirstMessage]
    );

     
    const isConversationLocked =
        activeConversation?.consentStatus === "Completed" ||
        activeConversation?.locked === true;




    const canSend = React.useMemo(
        () =>
            !isConversationLocked &&
            underageGate.approved !== false &&
            (
                activeConversation?.status === "accepted" ||
                (activeConversation?.status === "pending" &&
                    activeConversation?.initiated_by === parsedActorId &&
                    !actorHasSentFirstMessage)
            ),
        [
            activeConversation,
            parsedActorId,
            actorHasSentFirstMessage,
            isConversationLocked,
            underageGate.approved,
        ]
    );



    const getPartnerId = (conv) => {
        if (!conv) return null;
        const p1 = parseInt(conv.participant1_id, 10);
        const p2 = parseInt(conv.participant2_id, 10);
        if (!p1 || !p2) return null;
        return p1 === parsedActorId ? p2 : p1;
    };



    




    const handleTyping = (e) => {
        const value = e.target.value;
        setNewMessage(value);

        if (
            !activeConversation ||
            isConversationLocked ||
            activeConversation.status !== "accepted" ||
            !socket.current?.connected
        ) {
            return;
        }



        const now = Date.now();
        if (now - lastTypedRef.current > 1000) {
            socket.current.emit("typing", {
                conversationId: activeConversation.id,
                senderId: parsedActorId,
            });
            lastTypedRef.current = now;
        }

        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => {
            socket.current.emit("stopTyping", {
                conversationId: activeConversation.id,
                senderId: parsedActorId,
            });
        }, 3000);
    };


    const handleSelectConversation = (conv) => {
        setMobileListMode(false);
        setActiveConversation(conv);
    };






    useEffect(() => {
        if (!role) return;

        setConversations([]);
        setActiveConversation(null);
        setMessages([]);
        setMobileListMode(false);
    }, [role]);

    useEffect(() => {
        setIsTyping(false);
    }, [activeConversation?.id]);

    useEffect(() => {
        activeConvIdRef.current = activeConversation?.id || null;
    }, [activeConversation?.id]);

    useEffect(() => {
        if (!resolvedSelectedPlayerId) {
            startGuardRef.current = null;
        }
    }, [resolvedSelectedPlayerId]);





    useEffect(() => {
        if (
            !isMobile &&
            !activeConversation &&
            conversations.length > 0 &&
            !resolvedOpenConversationId &&
            !resolvedSelectedPlayerId
        ) {
            setActiveConversation(conversations[0]);
        }
    }, [
        conversations,
        activeConversation,
        resolvedOpenConversationId,
        resolvedSelectedPlayerId,
        isMobile
    ]);



    // ✅ Always keep activeConversation enriched (partnerName, partnerSlug, etc.)
    useEffect(() => {
        if (!activeConversation?.id) return;

        const enriched = conversations.find(
            (c) => String(c.id) === String(activeConversation.id)
        );
        if (!enriched) return;

        const needsUpgrade =
            !activeConversation.partnerName && enriched.partnerName ||
            !activeConversation.partnerProfileSlug && enriched.partnerProfileSlug ||
            !activeConversation.partnerSlug && enriched.partnerSlug;

        // Also upgrade if partnerName differs
        const nameDiffers =
            enriched.partnerName && enriched.partnerName !== activeConversation.partnerName;

        if (needsUpgrade || nameDiffers) {
            setActiveConversation(enriched);
        }
    }, [conversations, activeConversation?.id]);






    /* -------------------- Socket Setup -------------------- */

    useEffect(() => {
        if (!parsedActorId || !role) return;

        socket.current = io(BASE_URL);
        socket.current.emit("registerUser", {
            userId: parsedActorId,
            role,
        });

        return () => {
            if (socket.current) {
                socket.current.disconnect();
                socket.current = null;
            }
        };
    }, [parsedActorId, role]);


    /* -------------------- Load Conversations -------------------- */

    useEffect(() => {
        if (!parsedActorId || !role) return;

        apiFetch(`/api/conversations/${parsedActorId}?role=${role}`)
            .then(async (res) => {
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || `Failed to load conversations (${res.status})`);
                }
                return res.json();
            })
            .then((data) => {
                const all = [...(data?.chats || []), ...(data?.requests || [])];
                setConversations(sortConvs(all));
            })
            .catch((err) => console.error("[RoleChats] Error loading conversations:", err));
    }, [parsedActorId, role]);


    /* -------------------- Consent Lock Listener -------------------- */

    useEffect(() => {
        const s = socket.current;
        if (!s) return;

        const onConversationLocked = ({ conversationId }) => {
            if (String(activeConversation?.id) === String(conversationId)) {
                // 🔒 Leave room immediately
                s.emit("leaveConversation", conversationId);
                prevConvIdRef.current = null;

                setIsTyping(false);

                setConversations(prev =>
                    prev.map(c =>
                        String(c.id) === String(conversationId)
                            ? { ...c, consentStatus: "Completed", locked: true }
                            : c
                    )
                );


                setActiveConversation(prev =>
                    prev
                        ? { ...prev, consentStatus: "Completed", locked: true }
                        : prev
                );


            }
        };

        s.on("conversationLocked", onConversationLocked);

        return () => {
            s.off("conversationLocked", onConversationLocked);
        };
    }, [activeConversation?.id]);




    /* -------------------- Open Conversation by ID -------------------- */

    useEffect(() => {
        if (!resolvedOpenConversationId) return;
        if (isMobile && mobileListMode) return;

        const match = conversations.find(
            (c) => String(c.id) === String(resolvedOpenConversationId)
        );

        if (match && String(activeConversation?.id) !== String(match.id)) {
            setActiveConversation(match);
        }
    }, [resolvedOpenConversationId, conversations, activeConversation?.id, isMobile, mobileListMode]);


    /* -------------------- Start / Select by Player -------------------- */

    useEffect(() => {
        if (!resolvedSelectedPlayerId) return;
        if (!parsedActorId || !role) return;
        if (isMobile && mobileListMode) return;

        const spid = parseInt(resolvedSelectedPlayerId, 10);
        if (!spid) return;

        // ✅ If current active convo is already with this player, do nothing
        const ap1 = parseInt(activeConversation?.participant1_id, 10);
        const ap2 = parseInt(activeConversation?.participant2_id, 10);
        if (activeConversation && (ap1 === spid || ap2 === spid)) return;

        // ✅ Run only once per selected player id
        if (startGuardRef.current === spid) return;
        startGuardRef.current = spid;

        // ✅ Find existing convo specifically between actor and this player
        const actor = parseInt(parsedActorId, 10);
        const existing = conversations.find((c) => {
            const p1 = parseInt(c.participant1_id, 10);
            const p2 = parseInt(c.participant2_id, 10);
            return (p1 === actor && p2 === spid) || (p2 === actor && p1 === spid);
        });

        if (existing) {
            setActiveConversation((prev) =>
                String(prev?.id) === String(existing.id) ? prev : existing
            );
            return;
        }

        apiFetch(`/api/conversations/start`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                participant1_id: parsedActorId,
                participant2_id: spid,
                initiated_by: parsedActorId,
                initiatorRole: role,
            }),
        })
            .then(async (res) => {
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || `Failed to start conversation (${res.status})`);
                }
                return res.json();
            })
            .then((newConv) => {
                if (!newConv?.id) return;

                setConversations((prev) => {
                    const exists = prev.some((c) => String(c.id) === String(newConv.id));

                    const next = sortConvs(
                        exists
                            ? prev.map((c) =>
                                String(c.id) === String(newConv.id) ? { ...c, ...newConv } : c
                            )
                            : [...prev, newConv]
                    );

                    // ✅ IMPORTANT: set active convo from "next" because it has partnerName
                    const enriched =
                        next.find((c) => String(c.id) === String(newConv.id)) || newConv;

                    setActiveConversation(enriched);
                    return next;
                });
            })

            .catch((err) => console.error("[RoleChats] Error starting conversation:", err));

    }, [resolvedSelectedPlayerId, parsedActorId, role, conversations, activeConversation?.id, isMobile, mobileListMode]);





    /* -------------------- Partner Status -------------------- */

    useEffect(() => {
        if (!activeConversation) {
            setPartnerStatus(null);
            return;
        }

        const p1 = parseInt(activeConversation.participant1_id, 10);
        const p2 = parseInt(activeConversation.participant2_id, 10);
        const partnerId = p1 === parsedActorId ? p2 : p1;

        if (!partnerId) {
            setPartnerStatus(null);
            return;
        }

        apiFetch(`/api/online-status/${partnerId}`)
            .then(async (res) => {
                if (!res.ok) return null;
                return res.json();
            })
            .then((data) => {
                setPartnerStatus(data || null);
            })
            .catch((err) => {
                console.error("[RoleChats] Error fetching partner status:", err);
                setPartnerStatus(null);
            });
    }, [activeConversation, parsedActorId]);




    useEffect(() => {
        if (!activeConversation?.id) return;

        // Partner is the player in RoleChats (agent/scout/club ↔ player)
        const playerId = getPartnerId(activeConversation);
        if (!playerId) return;

        // Only relevant if partner is underage — if you don't have age in convo,
        // we still check; backend will return approved/status based on existing requests.
        const qs = new URLSearchParams({
            senderRole: role,
            senderId: String(parsedActorId),
            playerId: String(playerId),
        }).toString();

        setUnderageGate((p) => ({ ...p, loading: true }));

        apiFetch(`/api/underage-message-request/check?${qs}`)
            .then(async (res) => {
                const data = await res.json().catch(() => null);
                if (!res.ok) {
                    const errText = await res.text().catch(() => "");
                    console.warn("[RoleChats] underage consent check failed:", res.status, errText);
                    // Fail open, but keep a status so UI can show something if needed
                    setUnderageGate({ loading: false, approved: true, status: null });
                    return;
                }

                setUnderageGate({
                    loading: false,
                    approved: !!data?.approved,
                    status: data?.status ?? null,
                });
            })
            .catch(() => {
                // Fail open
                setUnderageGate({ loading: false, approved: true, status: null });
            });
    }, [activeConversation?.id, parsedActorId, role]);



    /* -------------------- Join / Leave Rooms -------------------- */

    /* -------------------- Join / Leave Rooms (CONSENT SAFE) -------------------- */

    useEffect(() => {
        if (!socket.current) return;

        if (
            prevConvIdRef.current &&
            prevConvIdRef.current !== activeConversation?.id
        ) {
            socket.current.emit("leaveConversation", prevConvIdRef.current);
        }

        if (activeConversation?.id && !isConversationLocked) {
            socket.current.emit("joinConversation", {
                conversationId: activeConversation.id,
                role,
            });

            prevConvIdRef.current = activeConversation.id;
        }



    }, [activeConversation?.id, role, isConversationLocked]);




    /* -------------------- Typing Indicators -------------------- */

    useEffect(() => {
        const s = socket.current;
        if (!s) return;

        const onTyping = ({ conversationId, senderId }) => {
            if (
                conversationId === activeConversation?.id &&
                senderId !== parsedActorId
            ) {
                setIsTyping(true);
            }
        };

        const onStopTyping = ({ conversationId, senderId }) => {
            if (
                conversationId === activeConversation?.id &&
                senderId !== parsedActorId
            ) {
                setIsTyping(false);
            }
        };

        s.on("typing", onTyping);
        s.on("stopTyping", onStopTyping);

        return () => {
            s.off("typing", onTyping);
            s.off("stopTyping", onStopTyping);
        };
    }, [activeConversation?.id, parsedActorId]);



    /* -------------------- Load Messages -------------------- */

    useEffect(() => {
        const convId = activeConversation?.id;
        if (!convId || !parsedActorId) return;

        // ✅ Prevent infinite re-firing for the same conversation
        if (lastMarkReadRef.current === convId) return;
        lastMarkReadRef.current = convId;

        // Mark read once per conversation open
        apiFetch(`/api/messages/mark-read/${convId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: parsedActorId }),
        })

            .then((res) => {
                if (!res.ok) return;

                setConversations((prev) =>
                    prev.map((c) => (c.id === convId ? { ...c, unreadCount: 0 } : c))
                );
            })
            .catch(() => { });


        // Load messages
        apiFetch(`/api/messages/${convId}`)
            .then(async (res) => {
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || `Failed to load messages (${res.status})`);
                }
                return res.json();
            })
            .then((msgs) => {
                setMessages(msgs);
                setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
                }, 0);
            })

            .catch((err) => console.error("[RoleChats] Error loading messages:", err));

    }, [activeConversation?.id, parsedActorId]);


    /* -------------------- Realtime Messages -------------------- */

    /* -------------------- Realtime Messages (DEDUPED) -------------------- */

    useEffect(() => {
        const s = socket.current;
        if (!s || !activeConversation?.id) return;

        const onNewMessage = (msg) => {
            // Only handle messages for the active conversation
            if (String(msg.conversation_id) !== String(activeConvIdRef.current)) {
                // Still update sidebar metadata
                setConversations((list) =>
                    sortConvs(
                        list.map((c) =>
                            String(c.id) === String(msg.conversation_id)
                                ? {
                                    ...c,
                                    lastMessage: msg.message,
                                    lastMessageTime: msg.created_at,
                                    unreadCount: (c.unreadCount || 0) + 1

                                }
                                : c
                        )
                    )
                );
                return;
            }

            // 🔒 Prevent duplicate messages
            setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id)) return prev;
                return [...prev, msg];
            });

            // 🔄 Update sidebar metadata
            setConversations((list) =>
                sortConvs(
                    list.map((c) =>
                        String(c.id) === String(msg.conversation_id)
                            ? {
                                ...c,
                                lastMessage: msg.message,
                                lastMessageTime: msg.created_at,
                                unreadCount: 0,
                            }
                            : c
                    )
                )
            );

            // ⬇️ Auto-scroll
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 0);
        };

        s.on("newMessage", onNewMessage);

        return () => {
            s.off("newMessage", onNewMessage);
        };
    }, [activeConversation?.id]);




    /* -------------------- Send Message -------------------- */

    const handleSend = async () => {
        if (!activeConversation || isConversationLocked) return;
        if (!canSend || !newMessage.trim()) return;



        const res = await apiFetch(`/api/messages/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                conversationId: activeConversation.id,
                senderId: parsedActorId,
                message: newMessage,
            }),
        });

        if (!res.ok) {
            const text = await res.text().catch(() => "");
            console.error("[RoleChats] Send failed:", text || res.status);
        }


        setNewMessage("");
    };

    /* -------------------- UI -------------------- */

    return (
        <div className={`role-chats agent-chats ${role}-chats`}>
            <div className="inbox-container">
                {(!isMobile || !activeConversation) && (
                    <div className="chat-list">
                        <div className="chat-header">
                            <h3>Messages</h3>
                        </div>

                        {conversations.map((conv) => (
                            <div
                                key={conv.id}
                                className={`chat-preview ${activeConversation?.id === conv.id ? "active" : ""}`}
                                onClick={() => handleSelectConversation(conv)}
                            >
                                <strong>{conv.partnerName || "Unknown"}</strong>
                                <div className="preview-snippet">
                                    {conv.lastMessage ? `${conv.lastMessage.slice(0, 40)}…` : ""}
                                </div>
                                {conv.unreadCount > 0 && (
                                    <div className="unread-badge">{conv.unreadCount}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {(!isMobile || activeConversation) && (
                    <div className="chat-window">
                        {activeConversation ? (
                            <>
                                {isMobile && (
                                    <div className="mobile-chat-topbar">
                                        <button
                                            className="mobile-back-btn"
                                            onClick={() => {
                                                setMobileListMode(true);
                                                setActiveConversation(null);
                                            }}
                                        >
                                            ← Back
                                        </button>
                                    </div>
                                )}

                                <div
                                    className="chat-partner-header"
                                    onClick={() => {
                                        const partnerSlug = getPartnerSlug(activeConversation);

                                        if (!partnerSlug) {
                                            console.error("❌ Missing partner slug in conversation:", activeConversation);
                                            return;
                                        }

                                        navigateToPlayerProfile(activeConversation);
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="partner-name">
                                        <strong>{activeConversation?.partnerName || "Unknown"}</strong>
                                    </div>

                                    <div
                                        className={`online-status ${partnerStatus?.is_online ? "online" : "offline"}`}
                                    >
                                        {partnerStatus?.is_online
                                            ? "Online"
                                            : partnerStatus?.last_seen
                                                ? `Last seen ${new Date(partnerStatus.last_seen).toLocaleTimeString(undefined, {
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                })}`
                                                : "Offline"}
                                    </div>
                                </div>

                                <div className="chat-messages">
                                    {messages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className={`chat-message ${parseInt(msg.sender_id, 10) === parsedActorId
                                                    ? "sent"
                                                    : "received"
                                                }`}
                                        >
                                            <div className="message-text">{msg.message}</div>
                                        </div>
                                    ))}
                                    {isTyping && (
                                        <div className="chat-message typing-indicator">
                                            <div className="message-text">Typing…</div>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {isConversationLocked ? (
                                    <div className="chat-locked-banner">
                                        <p>
                                            🔒 Messaging with this player has been locked because parental consent has expired.
                                        </p>
                                        <button
                                            className="go-to-profile-btn"
                                            onClick={() => navigateToPlayerProfile(activeConversation)}
                                        >
                                            Go to Player Profile
                                        </button>
                                    </div>
                                ) : underageGate.loading ? (
                                    <div className="chat-blocked-message">
                                        <p className="blocked-info">Checking consent status…</p>
                                    </div>
                                ) : underageGate.approved === false && underageGate.status ? (
                                    <div className="chat-locked-banner">
                                        <p>
                                            🔒 {underageGate.status === "Completed"
                                                ? "Your consent to message this player has expired. Please request consent again."
                                                : underageGate.status === "Pending"
                                                    ? "Your request to message this underage player is pending approval."
                                                    : underageGate.status === "Rejected"
                                                        ? "Your request to message this underage player was rejected."
                                                        : `Messaging is restricted. Consent status: ${underageGate.status}.`}
                                        </p>

                                        <button
                                            className="go-to-profile-btn"
                                            onClick={() => navigateToPlayerProfile(activeConversation)}
                                        >
                                            View Profile
                                        </button>
                                    </div>
                                ) : canSend ? (
                                    <div className="chat-input">
                                        <input
                                            value={newMessage}
                                            placeholder="Type a message…"
                                            onChange={handleTyping}
                                            onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                        />
                                        <button onClick={handleSend}>Send</button>
                                    </div>
                                ) : isPendingAndInitiatedByActor ? (
                                    <div className="chat-blocked-message">
                                        <p className="blocked-info">
                                            You’ve sent a request. You’ll be able to message once it’s accepted.
                                        </p>
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="no-chat-selected">
                                Select a conversation to start chatting
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoleChats;
